import React, { useState, useEffect } from 'react';
import { Box, Button, VStack, Text, Code, Heading, Divider, Badge } from '@chakra-ui/react';
import { websocketService } from '../services/websocketService';
import { WS_BASE_URL, WS_TOPICS } from '../config/websocket';

export const WebSocketTest: React.FC = () => {
  const [connectionStatus, setConnectionStatus] = useState<string>('Disconnected');
  const [logs, setLogs] = useState<string[]>([]);
  const [sockJSInstance, setSockJSInstance] = useState<any>(null);

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, `[${timestamp}] ${message}`]);
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (sockJSInstance) {
        sockJSInstance.close();
      }
      disconnect();
    };
  }, []);

  const testConnection = () => {
    addLog(`Attempting to connect to: ${WS_BASE_URL}`);
    setConnectionStatus('Connecting...');
    
    try {
      websocketService.connect();
      addLog('WebSocket connect() called successfully');
      setConnectionStatus('Connected');
      
      // Test subscription
      setTimeout(() => {
        addLog('Testing subscription to event 1 markets...');
        addLog(`Topic: ${WS_TOPICS.MARKET_UPDATES(1)}`);
        
        websocketService.subscribeToMarketUpdates(1, (market) => {
          addLog(`Received market update: ${JSON.stringify(market)}`);
        });
        
        // Also subscribe to all markets
        addLog(`All markets topic: ${WS_TOPICS.ALL_MARKETS}`);
        websocketService.subscribeToAllMarketUpdates((market) => {
          addLog(`Received global market update: ${JSON.stringify(market)}`);
        });
      }, 2000);
    } catch (error) {
      addLog(`Connection error: ${error}`);
      setConnectionStatus('Error');
    }
  };

  const testDirectSockJS = () => {
    try {
      // Import SockJS dynamically
      import('sockjs-client').then(SockJSModule => {
        const SockJS = SockJSModule.default;
        addLog(`Testing direct SockJS connection to ${WS_BASE_URL}`);
        
        // Create SockJS instance
        const sockjs = new SockJS(WS_BASE_URL);
        setSockJSInstance(sockjs);
        
        sockjs.onopen = () => {
          addLog('SockJS connection opened directly');
          setConnectionStatus('SockJS Connected');
        };
        
        sockjs.onmessage = (e: any) => {
          addLog(`SockJS message received: ${e.data}`);
        };
        
        sockjs.onclose = (e: any) => {
          addLog(`SockJS connection closed: ${e.reason}`);
          setConnectionStatus('SockJS Disconnected');
        };
        
        sockjs.onerror = (e: any) => {
          addLog(`SockJS error: ${e}`);
          setConnectionStatus('SockJS Error');
        };
      });
    } catch (error) {
      addLog(`SockJS direct connection error: ${error}`);
    }
  };

  const disconnect = () => {
    websocketService.disconnect();
    setConnectionStatus('Disconnected');
    addLog('Disconnected from WebSocket');
    
    // Also close direct SockJS connection if exists
    if (sockJSInstance) {
      sockJSInstance.close();
      setSockJSInstance(null);
    }
  };

  return (
    <Box p={4}>
      <VStack spacing={4} align="stretch">
        <Heading size="md">WebSocket Connection Test</Heading>
        
        <Box>
          <Text>WebSocket URL: <Code>{WS_BASE_URL}</Code></Text>
          <Text>Status: <Badge colorScheme={
            connectionStatus === 'Connected' || connectionStatus === 'SockJS Connected' ? 'green' : 'red'
          }>{connectionStatus}</Badge></Text>
        </Box>
        
        <Box>
          <Heading size="sm" mb={2}>STOMP over SockJS Test</Heading>
          <Button onClick={testConnection} colorScheme="blue" mr={2}>Test STOMP Connection</Button>
          <Button onClick={disconnect} colorScheme="red">Disconnect</Button>
        </Box>
        
        <Divider />
        
        <Box>
          <Heading size="sm" mb={2}>Direct SockJS Test</Heading>
          <Button onClick={testDirectSockJS} colorScheme="teal" mr={2}>Test Direct SockJS</Button>
          <Button onClick={disconnect} colorScheme="red">Disconnect</Button>
        </Box>
        
        <Divider />
        
        <Box>
          <Text fontWeight="bold">Connection Logs:</Text>
          <Box
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            p={2}
            maxH="400px"
            overflowY="auto"
            bg="gray.50"
          >
            {logs.map((log, index) => (
              <Text key={index} fontSize="sm" fontFamily="monospace">{log}</Text>
            ))}
          </Box>
        </Box>
      </VStack>
    </Box>
  );
};
